import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import { resolveVisibleMvrAnnouncement } from "../../helper/mvrAnnouncement";
import {
    Dialog, DialogTitle, DialogContent, DialogContentText,
    DialogActions, Button, IconButton
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

export class MvrIntroductionDialog extends Component {
    state = { dismissed: {}, errors: {} };
    generation = 0;

    getAnnouncementContext = () => resolveVisibleMvrAnnouncement(this.props.profileStore, this.props.organisationStore);
    contextKey = () => {
        const context = this.getAnnouncementContext();
        return context ? String(context.profileId) : "";
    };
    isOpen = () => {
        const context = this.getAnnouncementContext();
        return !!(context && context.mvrAnnouncementRead === false &&
            !this.state.dismissed[String(context.profileId)]);
    };

    componentDidMount() {
        this.mounted = true;
        this.lastContext = this.contextKey();
        this.reportVisibility();
    }

    componentDidUpdate() {
        const key = this.contextKey();
        if (key !== this.lastContext) {
            this.generation += 1;
            this.lastContext = key;
        }
        this.reportVisibility();
    }

    componentWillUnmount() {
        this.mounted = false;
        this.generation += 1;
    }

    reportVisibility = () => {
        const open = this.isOpen();
        const key = this.contextKey();
        const signature = `${key}:${open}`;
        if (signature !== this.lastVisibility) {
            this.lastVisibility = signature;
            if (this.props.onVisibilityChange) this.props.onVisibilityChange(open, key);
        }
    };

    handleClose = () => {
        const context = this.getAnnouncementContext();
        if (!context) return;
        const key = String(context.profileId);
        this.setState(({ dismissed }) => ({ dismissed: { ...dismissed, [key]: true } }));
        if (this.props.onClose) this.props.onClose();
    };

    currentGuard = () => {
        const key = this.contextKey();
        const generation = this.generation;
        const store = this.props.profileStore;
        const session = store.mvrAnnouncements.session;
        const actor = store.currentProfile?.userId;
        return () => this.mounted && generation === this.generation && key === this.contextKey() &&
            session === store.mvrAnnouncements.session && actor === store.currentProfile?.userId;
    };

    handleAcknowledge = async () => {
        const context = this.getAnnouncementContext();
        if (!context || !this.isOpen()) return;
        const store = this.props.profileStore.mvrAnnouncements;
        const key = String(context.profileId);
        if (store.saving.get(key)) return;
        const isCurrent = this.currentGuard();
        this.setState(({ errors }) => ({ errors: { ...errors, [key]: null } }));
        try {
            await store.acknowledge(context);
            if (!isCurrent()) return;
            this.handleClose();
            // A failed refresh is distinct from a failed save; never repeat the POST.
            await store.refresh(context, isCurrent);
        } catch (error) {
            if (!isCurrent()) return;
            const data = error?.response?.data;
            const message = typeof data?.errors === "string" ? data.errors :
                data?.message || error?.message || "Could not save your acknowledgement. Please try again.";
            this.setState(({ errors }) => ({ errors: { ...errors, [key]: message } }));
        }
    };

    render() {
        const context = this.getAnnouncementContext();
        const key = context && String(context.profileId);
        const store = this.props.profileStore.mvrAnnouncements;
        const saving = !!(key && store.saving.get(key));
        const refreshError = key && store.refreshErrors.get(key);
        return (
            <React.Fragment>
            {refreshError && <div role="status" style={{ padding: "8px 0" }}>
                {refreshError}{" "}
                <Button disabled={store.refreshing.get(key)}
                    onClick={() => store.refresh(context, this.currentGuard())}>
                    Retry profile refresh
                </Button>
            </div>}
            <Dialog
                open={this.isOpen()}
                onClose={this.handleClose}
                aria-labelledby="mvr-introduction-title"
                aria-describedby="mvr-introduction-description"
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: { borderRadius: 12, margin: 16, width: "calc(100% - 32px)", maxWidth: 520 }
                }}
            >
                <DialogTitle disableTypography style={{ padding: "20px 24px 12px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <h2 id="mvr-introduction-title" style={{
                            margin: 0, flex: 1, fontSize: 22, fontWeight: 700,
                            lineHeight: 1.3, color: "#1f2430"
                        }}>
                            Introducing super fund membership checks
                        </h2>
                        <IconButton
                            aria-label="Close MVR introduction"
                            onClick={this.handleClose}
                            style={{ padding: 6, flexShrink: 0 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </DialogTitle>
                <DialogContent id="mvr-introduction-description" style={{ padding: "8px 24px 12px" }}>
                    <DialogContentText style={{ color: "#4b5563", lineHeight: 1.6 }}>
                        HG Super Pay now supports <strong>Member Verification Requests (MVR)</strong>, a SuperStream check that helps confirm an artist or player’s fund can accept super contributions.
                    </DialogContentText>
                    <DialogContentText component="ul" style={{ color: "#4b5563", lineHeight: 1.6, paddingLeft: 28, marginTop: 0, marginBottom: 16 }}>
                        <li><strong>New employee?</strong> Selecting <strong>Sync Employee</strong> automatically starts verification.</li>
                        <li><strong>Details changed?</strong> Update the employee in HG Super Pay and complete the required verification.</li>
                        <li><strong>Payments already working?</strong> No additional check is needed if their details haven’t changed.</li>
                    </DialogContentText>
                    <DialogContentText style={{ color: "#4b5563", lineHeight: 1.6 }}>
                        <strong>Pending checks usually take 24–48 hours.</strong> Super payments for that person must wait until verification completes.
                    </DialogContentText>
                    <DialogContentText style={{ color: "#4b5563", lineHeight: 1.6 }}>
                        Some funds <strong>do not yet support MVR</strong>. Funds have until <strong>28 February 2027</strong> to implement it. “Not supported” does not mean the member’s details are incorrect.
                    </DialogContentText>
                    <DialogContentText style={{ lineHeight: 1.6, marginBottom: 0 }}>
                        <a
                            href="https://support.hotgiggity.com/support/solutions/articles/51000532826-mvr-how-to-verify-super-fund-details-in-hg-super-pay/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "underline" }}
                        >
                            Learn more about super fund verification
                        </a>
                    </DialogContentText>
                    {key && this.state.errors[key] &&
                        <p role="alert" style={{ color: "#b71c1c" }}>{this.state.errors[key]}</p>}
                </DialogContent>
                <DialogActions style={{ padding: "12px 24px 24px", margin: 0 }}>
                    <Button onClick={this.handleClose} style={{ textTransform: "none" }}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={this.handleAcknowledge}
                        disabled={saving}
                        style={{ backgroundColor: "#ff0c9c", color: "#fff", textTransform: "none" }}
                    >
                        {saving ? "Saving…" : "I understand"}
                    </Button>
                </DialogActions>
            </Dialog>
            </React.Fragment>
        );
    }
}

export default inject("profileStore", "organisationStore")(observer(MvrIntroductionDialog));
